import React from 'react';
import { css } from '@emotion/css';
import BannerImage from './images/banner@1x.jpg';
import { useGetUserAccountDetails } from '@/apiHooks/userAccount.hook';
import { GRAY100 } from '@/domain/colors';
import { useIntl } from '@/i18n/hooks/useIntl';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';

const styles = {
  close: {
    cursor: 'pointer',
    position: 'absolute',
    right: '10px',
    zIndex: 1,
    color: '#f5f5f5',
    border: 'none',
    top: '10px',
    borderRadius: '900px',
    display: 'flex',
    padding: '5px',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '22px',
    background: 'none',
    '@media (min-width: 576px)': {
      i: {
        transition: 'transform 0.5s ease-in-out',
        '&:hover': {
          transform: 'rotate(-180deg)',
        },
      },
      '&:hover, &:active': {
        color: `${GRAY100}`,
        background: 'transparent',
      },
    },
  },
  content: {
    background: 'rgb(51, 51, 51)',
    color: `${GRAY100}`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: 'calc(100% - 300px)',
    minHeight: '245px',
    height: '45%',
    '@media (min-width: 576px)': {
      height: 'calc(100% - 340px)',
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 'normal',
    letterSpacing: '0',
    lineHeight: '29px',
    textAlign: 'center',
    paddingTop: '40px',
    paddingBottom: '24px',
    marginBottom: '0',
    color: 'inherit',
    '@media (min-width: 576px)': {
      fontSize: '22px',
      lineHeight: '28px',
    },
  },
  description: {
    fontSize: '20px',
    fontWeight: 'normal',
    letterSpacing: '0',
    lineHeight: '24px',
    textAlign: 'center',
    paddingBottom: '38px',
    '@media (min-width: 576px)': {
      paddingBottom: '28px',
    },
  },
  image: {
    background: `url(${BannerImage}) no-repeat`,
    minHeight: '300px',
    maxHeight: 'calc(100% - 245px)',
    height: '55%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    '@media (min-width: 576px)': {
      height: '340px',
    },
  },
  button: {
    marginBottom: '25px',
    '@media (min-width: 576px)': {
      marginBottom: '30px',
    },
  },
  modal: {
    maxWidth: '760px',
    position: 'relative',
    backgroundColor: 'rgb(51, 51, 51)',
    flex: 1,
    overflowY: 'auto',
    zIndex: 0,
    '@media (max-width: 767px)': {
      height: '100%',
    },
    '@media screen and (max-height: 710px)': {
      height: '100%',
    },
  },
} as const;

interface BannerProps {
  variant: 'mieten' | 'wohnen';
  onClose: () => void;
}

export const Banner = ({ onClose, variant }: BannerProps) => {
  const { userAccountDetails } = useGetUserAccountDetails();
  const { t } = useIntl();

  const showDescription = () => {
    if (variant == 'mieten') {
      return (
        <p className={`${css(styles.description)}`} data-test-ctx='mieterplus-test'>
          {t(
            {
              id: 'welcomeModal.banner.title',
              defaultMessage: 'Ab sofort kannst du alle Suchen+ für Miete Vorteile nutzen!',
            },
            {
              BuyerPlusLogo: <PlusBadge variant='rent' whiteLogo={true} />,
            },
          )}
        </p>
      );
    } else {
      return (
        <p className={`${css(styles.description)}`} data-test-ctx='wohnenplus-test'>
          {t(
            {
              id: 'welcomeModal.banner.title',
              defaultMessage: 'Ab sofort kannst du alle Wohnen+ Vorteile nutzen!',
            },
            {
              BuyerPlusLogo: <PlusBadge variant='living' whiteLogo={true} />,
            },
          )}
        </p>
      );
    }
  };
  return (
    <div data-testid='bannerModal' className={`${css(styles.modal)}`}>
      <div className={`${css(styles.image)}`} />
      <div className={`${css(styles.content)}`}>
        <h3 className={`${css(styles.title)}`} data-test-ctx='welcome-profile-test'>
          {userAccountDetails?.firstName
            ? t(
                {
                  id: 'welcomeModal.banner.welcomeText',
                  defaultMessage: 'Herzlichen Glückwunsch',
                },
                {
                  FirstName: userAccountDetails.firstName,
                },
              )
            : t({
                id: 'welcomeModal.banner.welcomeTextNoName',
                defaultMessage: 'Herzlichen Glückwunsch',
              })}
        </h3>
        {showDescription()}
        <button className={`${css(styles.button)} button-rounded button-primary`} onClick={onClose}>
          {t({ id: 'common.buttons.next', defaultMessage: 'Weiter' })}
        </button>
      </div>
    </div>
  );
};
